import React from 'react';
import ListViewDateField from '../ListViewDate/ListViewDate';
import ListViewFilters from '../../filters/ListViewFilters';
import ListViewModeToggle from '../ListViewModeToggle';
import ListViewPastBookingsToggle from '../ListViewPastBookingsToggle';
import ListViewRefresh from '../ListViewRefresh/ListViewRefresh';
import ListViewSetsToggle from '../ListViewSetsToggle';
import {
  DisplayRow,
  DisplaySwitchSlot,
  ToolbarBand,
  ToolbarFrame,
  ToolbarLabel,
  ToolbarRow,
  ToolbarRowContent
} from './ListViewToolbar.style';
import type {
  ActiveFilter,
  FilterTreeItem,
  ListViewDate,
  ListViewMode,
  RefreshControl,
  TimeFormatSetting
} from '../../types';

interface ListViewToolbarProps {
  date: ListViewDate;
  onDateChange: (date: ListViewDate) => void;
  filterTree: FilterTreeItem[];
  activeFilters: ActiveFilter[];
  onAddFilter: (filter: ActiveFilter) => void;
  onUpdateFilter: (filter: ActiveFilter) => void;
  onRemoveFilter: (filterID: string) => void;
  viewMode: ListViewMode;
  onViewModeChange: (viewMode: ListViewMode) => void;
  showChildren: boolean;
  onShowChildrenChange: (showChildren: boolean) => void;
  /* Whether past bookings are being left out, and whether that applies at
     all: it does on today only */
  hidePastBookings: boolean;
  onHidePastBookingsChange: (hidePastBookings: boolean) => void;
  canHidePastBookings: boolean;
  refreshControl: RefreshControl;
  timeFormatSetting: TimeFormatSetting;
}

/**
 * The controls of the list view, in two bands: what is included (the day and
 * the filters), and how it is shown and how fresh it is (the view mode, the
 * set members, the refresh controls).
 */
const ListViewToolbar = ({
  date,
  onDateChange,
  filterTree,
  activeFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  viewMode,
  onViewModeChange,
  showChildren,
  onShowChildrenChange,
  hidePastBookings,
  onHidePastBookingsChange,
  canHidePastBookings,
  refreshControl,
  timeFormatSetting
}: ListViewToolbarProps) => (
  <ToolbarFrame data-test-list-view-toolbar>
    {/* Which bookings are included. The day is a filter like the rest, but it
        gets a line of its own so it doesn't compete with the chips for width —
        and it stays visible, never folded into a menu */}
    <ToolbarBand $tone="scope">
      <ToolbarRow>
        <ToolbarLabel>{i18n('Date')}</ToolbarLabel>
        <ToolbarRowContent>
          <ListViewDateField date={date} onChange={onDateChange} />
        </ToolbarRowContent>
      </ToolbarRow>
      <ToolbarRow>
        <ToolbarLabel>{i18n('Filters')}</ToolbarLabel>
        <ToolbarRowContent>
          <ListViewFilters
            filterTree={filterTree}
            activeFilters={activeFilters}
            onAddFilter={onAddFilter}
            onUpdateFilter={onUpdateFilter}
            onRemoveFilter={onRemoveFilter}
          />
        </ToolbarRowContent>
      </ToolbarRow>
    </ToolbarBand>
    {/* How they are shown, and how fresh they are: what shapes the list on the
        left, what acts on it on the right, nothing in between */}
    <ToolbarBand $tone="display">
      <DisplayRow>
        <div tw="flex items-center order-1 min-w-0">
          <ListViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
        </div>
        {/* Both view modes answer to it — indented rows in the table, a
            section inside the card — so it stays put when the view mode
            changes */}
        <DisplaySwitchSlot $order={3} $orderFromMd={2}>
          <ListViewSetsToggle
            showChildren={showChildren}
            onShowChildrenChange={onShowChildrenChange}
          />
        </DisplaySwitchSlot>
        {/* Beside it, the other switch over the list on screen */}
        <DisplaySwitchSlot $order={4} $orderFromMd={3}>
          <ListViewPastBookingsToggle
            hidePastBookings={hidePastBookings}
            onHidePastBookingsChange={onHidePastBookingsChange}
            canHidePastBookings={canHidePastBookings}
          />
        </DisplaySwitchSlot>
        {/* Never shrinks: how fresh the list is, and the button acting on it,
            stay whole where the row runs out of room */}
        <div tw="flex items-center flex-none order-2 ml-auto pl-8 md:order-4">
          <ListViewRefresh
            refreshControl={refreshControl}
            timeFormatSetting={timeFormatSetting}
          />
        </div>
      </DisplayRow>
    </ToolbarBand>
  </ToolbarFrame>
);

export default ListViewToolbar;
